'use client';

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FileSpreadsheet, Plus, X, Trash2, Download, Eye, Send, CheckCircle, XCircle, Loader,
  MailOpen, MessageCircle, Mail, ArrowRightCircle,
} from 'lucide-react';
import api from '../../services/api';
import { SalespersonSelect } from '../../components/crm/billing/SalesFinance';
import ItemPicker from '../../components/crm/billing/ItemPicker';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { downloadQuotationPdf, previewQuotationPdf } from '../../utils/quotationPdf';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import RowActionsMenu from '../../components/crm/RowActionsMenu';
import SearchableSelect from '../../components/crm/SearchableSelect';
import BillingSliceModal from '../../components/crm/billing/BillingSliceModal';
import { SUPPORTED_CURRENCIES } from '../../constants/currencies';
import {
  fetchCrmQuotations, createCrmQuotation, deleteCrmQuotation,
  markCrmQuotationSent, markCrmQuotationRejected,
  sendCrmQuotationViaWhatsApp, sendCrmQuotationViaEmail, convertCrmQuotationToInvoice,
  fetchTaxProfiles,
} from '../../redux/crmSlice';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const KINDS = {
  quotation: { one: 'Quotation', many: 'Quotations', date: 'Valid Until' },
  proforma: { one: 'Proforma invoice', many: 'Proforma invoices', date: 'Valid Until' },
  sales_order: { one: 'Sales order', many: 'Sales orders', date: 'Deliver By' },
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'DRAFT' },
  { value: 'sent', label: 'SENT' },
  { value: 'viewed', label: 'VIEWED' },
  { value: 'accepted', label: 'ACCEPTED' },
  { value: 'rejected', label: 'REJECTED' },
  { value: 'expired', label: 'EXPIRED' },
  { value: 'partially_invoiced', label: 'PART INVOICED' },
  { value: 'converted', label: 'CONVERTED' },
];

const STATUS_BADGE = {
  draft: 'sf-badge-neutral',
  sent: 'sf-badge-warning',
  viewed: 'sf-badge-warning',
  accepted: 'sf-badge-success',
  rejected: 'sf-badge-error',
  expired: 'sf-badge-neutral',
  converted: 'sf-badge-success',
  partially_invoiced: 'sf-badge-warning',
};

const BLANK_FORM = {
  accountId: '', contactId: '', product: '', currency: 'INR', taxRate: 0, discountAmount: 0,
  discountType: 'flat', discountValue: 0, taxProfileId: '',
  placeOfSupply: '', customerGstin: '',
  issueDate: new Date().toISOString().slice(0, 10),
  validUntil: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  notes: '',
  lineItems: [{ description: '', hsnCode: '', quantity: 1, unitPrice: 0, remarks: '' }],
};



// Fills a line from an item, including the item's GST rate for that line.
function applyItem(f, idx, it) {
  return {
    ...f,
    lineItems: f.lineItems.map((x, i) => (i !== idx ? x : it
      ? { ...x, itemId: it._id, taxRate: it.taxRate ?? x.taxRate, description: it.description || it.name, hsnCode: it.hsnCode || x.hsnCode, unitPrice: it.salesPrice || x.unitPrice }
      : { ...x, itemId: null })),
  };
}

export default function QuotationsPage() {
  const dispatch = useDispatch();
  const { quotations, quotationsMeta, loading, taxProfiles } = useSelector(state => state.crm);
  const bulk = useBulkSelection();

  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [sendingWhatsAppId, setSendingWhatsAppId] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  // One page, three documents: quotations, proforma invoices, sales orders.
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = ['proforma', 'sales_order'].includes(searchParams.get('kind')) ? searchParams.get('kind') : 'quotation';
  const doc = KINDS[kind];
  const navigate = useNavigate();
  const convertTo = async (quotation, target) => {
    try {
      const { data } = await api.post(`/crm/quotations/${quotation._id}/convert-to`, { kind: target });
      alertService.success(`${KINDS[target].one} created`, data.quotation.quotationNumber);
      setSearchParams({ kind: target });
    } catch (err) {
      alertService.error('Not converted', err.response?.data?.message || 'Please try again');
    }
  };

  useEffect(() => {
    dispatch(fetchCrmQuotations({ status: statusFilter || undefined, limit: 100, kind }));
    dispatch(fetchTaxProfiles());
  }, [dispatch, statusFilter, kind]);

  const openCreate = async () => {
    setForm(BLANK_FORM);
    setShowModal(true);
    try {
      const [accRes, contactRes] = await Promise.all([
        api.get('/crm/accounts', { params: { limit: 200 } }),
        api.get('/crm/contacts', { params: { limit: 200 } }),
      ]);
      setAccounts(accRes.data.accounts || accRes.data || []);
      setContacts(contactRes.data.contacts || contactRes.data || []);
    } catch (err) {
      // Account/contact pickers stay empty — quotation creation still works with manual entry.
    }
  };

  const addLineItem = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, { description: '', hsnCode: '', quantity: 1, unitPrice: 0, remarks: '' }] }));
  const removeLineItem = (idx) => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, i) => i !== idx) }));
  const updateLineItem = (idx, field, value) => setForm(f => ({
    ...f,
    lineItems: f.lineItems.map((item, i) => i === idx ? { ...item, [field]: value } : item),
  }));

  const selectedTaxProfile = taxProfiles.find(p => p._id === form.taxProfileId);
  const subtotal = form.lineItems.reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0), 0);
  const rawDiscount = Number(form.discountValue) || 0;
  const discountAmount = Math.min(subtotal, form.discountType === 'percentage' ? subtotal * rawDiscount / 100 : rawDiscount);
  const taxableBase = subtotal - discountAmount;
  const effectiveTaxRate = selectedTaxProfile ? Number(selectedTaxProfile.rate) || 0 : Number(form.taxRate) || 0;
  // Without a tax profile each line can have its own GST rate (empty =
  // the document rate); the discount is shared across lines by value.
  const lineRates = form.lineItems.map((l) => (l.taxRate === '' || l.taxRate === null || l.taxRate === undefined ? Number(form.taxRate) || 0 : Number(l.taxRate) || 0));
  const mixedRates = !selectedTaxProfile && new Set(lineRates).size > 1;
  const taxAmount = selectedTaxProfile
    ? (selectedTaxProfile.isInclusive ? taxableBase * effectiveTaxRate / (100 + effectiveTaxRate) : taxableBase * effectiveTaxRate / 100)
    : form.lineItems.reduce((s, l, i) => s + (subtotal > 0 ? ((Number(l.quantity) || 0) * (Number(l.unitPrice) || 0) * taxableBase / subtotal) : 0) * lineRates[i] / 100, 0);
  const total = selectedTaxProfile?.isInclusive ? Math.max(0, taxableBase) : Math.max(0, taxableBase + taxAmount);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.accountId && !form.contactId) return alertService.error('Error', 'Select an account or contact');
    if (form.lineItems.some(i => !i.description.trim())) return alertService.error('Error', 'Every line item needs a description');

    setSaving(true);
    try {
      const payload = { ...form, kind };
      if (!payload.accountId) delete payload.accountId;
      if (!payload.contactId) delete payload.contactId;
      if (!payload.taxProfileId) delete payload.taxProfileId;
      await dispatch(createCrmQuotation(payload)).unwrap();
      alertService.success('Success', 'Quotation created');
      setShowModal(false);
    } catch (err) {
      alertService.error('Error', err || 'Failed to create quotation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (quotation) => {
    const confirmed = await alertService.confirm('Delete Quotation?', `Permanently delete "${quotation.quotationNumber}"?`, 'danger', 'Delete');
    if (!confirmed) return;
    try {
      await dispatch(deleteCrmQuotation(quotation._id)).unwrap();
      alertService.success('Deleted', 'Quotation deleted');
    } catch (err) {
      alertService.error('Error', err || 'Failed to delete quotation');
    }
  };

  const handleMarkSent = (quotation) => dispatch(markCrmQuotationSent(quotation._id));
  const handleMarkViewed = async (quotation) => {
    try {
      await api.post(`/crm/quotations/${quotation._id}/viewed`);
      dispatch(fetchCrmQuotations({ status: statusFilter || undefined, limit: 100, kind }));
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to mark quotation viewed');
    }
  };
  const handleMarkAccepted = async (quotation) => {
    try {
      const { data } = await api.post(`/crm/quotations/${quotation._id}/accept`);
      alertService.success('Quotation accepted', data.dealWon ? 'Its deal has been marked won.' : '');
      dispatch(fetchCrmQuotations({ status: statusFilter || undefined, limit: 100, kind }));
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to mark quotation accepted');
    }
  };
  const handleMarkRejected = async (quotation) => {
    const confirmed = await alertService.confirm('Reject Quotation?', `Mark "${quotation.quotationNumber}" as rejected?`, 'warning', 'Mark Rejected');
    if (confirmed) dispatch(markCrmQuotationRejected(quotation._id));
  };

  const handleSendViaWhatsApp = async (quotation) => {
    const confirmed = await alertService.confirm(
      'Send via WhatsApp?',
      `Send quotation "${quotation.quotationNumber}" as a PDF attachment over WhatsApp to ${quotation.accountId?.phone || quotation.contactId?.phoneNumber || 'this customer'}?`,
      'info', 'Send'
    );
    if (!confirmed) return;
    setSendingWhatsAppId(quotation._id);
    try {
      await dispatch(sendCrmQuotationViaWhatsApp({ id: quotation._id })).unwrap();
      alertService.success('Sent', 'Quotation sent over WhatsApp');
      dispatch(fetchCrmQuotations({ status: statusFilter || undefined, limit: 100, kind }));
    } catch (err) {
      alertService.error('Could not send', err || 'Failed to send quotation via WhatsApp');
    } finally {
      setSendingWhatsAppId(null);
    }
  };

  const handleSendViaEmail = async (quotation) => {
    const confirmed = await alertService.confirm(
      'Send via Email?',
      `Email quotation "${quotation.quotationNumber}" as a PDF attachment to ${quotation.accountId?.email || quotation.contactId?.email || 'this customer'}?`,
      'info', 'Send'
    );
    if (!confirmed) return;
    setSendingEmailId(quotation._id);
    try {
      await dispatch(sendCrmQuotationViaEmail({ id: quotation._id })).unwrap();
      alertService.success('Sent', 'Quotation emailed');
      dispatch(fetchCrmQuotations({ status: statusFilter || undefined, limit: 100, kind }));
    } catch (err) {
      alertService.error('Could not send', err || 'Failed to send quotation via email');
    } finally {
      setSendingEmailId(null);
    }
  };

  const [billingQuote, setBillingQuote] = useState(null);
  const handleConvertToInvoice = (quotation) => setBillingQuote(quotation);
  const submitConvert = async (billing) => {
    const quotation = billingQuote;
    setConvertingId(quotation._id);
    try {
      const res = await dispatch(convertCrmQuotationToInvoice({ id: quotation._id, billing })).unwrap();
      setBillingQuote(null);
      alertService.success('Invoice created', res.quotation?.status === 'partially_invoiced'
        ? `${res.invoice?.invoiceNumber} created — the rest of this quotation can be invoiced later`
        : `${res.invoice?.invoiceNumber} created from this quotation`);
    } catch (err) {
      alertService.error('Could not convert', err || 'Failed to convert quotation to invoice');
    } finally {
      setConvertingId(null);
    }
  };

  const handleDownloadPdf = async (quotation) => {
    setDownloadingId(quotation._id);
    await downloadQuotationPdf(quotation);
    setDownloadingId(null);
  };

  const handlePreviewPdf = async (quotation) => {
    setPreviewingId(quotation._id);
    await previewQuotationPdf(quotation);
    setPreviewingId(null);
  };

  return (
    <div className={PAGE}>
      <PageHeader title={doc.many} subtitle={<>{quotationsMeta.total} {doc.many.toLowerCase()}{kind === 'proforma' ? ' · requests for advance payment, not tax invoices' : ''}</>} icon={FileSpreadsheet} actions={<>
        <button onClick={openCreate} className="h-9 px-4 bg-[#0176D3] text-white rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2">
          <Plus size={14} /> New {doc.one}
        </button>
      </>} />

      <div className="flex gap-1 border-b border-[#D8DDE6] overflow-x-auto">
        {Object.entries(KINDS).map(([k, v]) => (
          <button key={k} type="button" onClick={() => setSearchParams(k === 'quotation' ? {} : { kind: k })}
            className={cn('shrink-0 whitespace-nowrap px-4 py-2 text-[13px] font-semibold border-b-2 -mb-px', kind === k ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#706E6B]')}>{v.many}</button>
        ))}
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3 flex items-center gap-3 shadow-sm">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-9 px-3 bg-[#F3F2F2] border border-[#C9C9C9] rounded text-[12px] font-bold uppercase outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="sf-table-container">
        <table className="sf-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={bulk.isAllSelected(quotations.map(q => q._id))}
                  onChange={() => bulk.toggleAll(quotations.map(q => q._id))}
                  aria-label="Select all quotations"
                  className="w-4 h-4 accent-[#0176D3] cursor-pointer"
                />
              </th>
              <th>Quotation #</th>
              <th>Customer</th>
              <th>Product</th>
              <th>Amount</th>
              <th>Status</th>
              <th>{doc.date}</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && quotations.length === 0 ? (
              <tr><td colSpan={8} className="py-16 text-center"><Loader className="mx-auto animate-spin text-[#0176D3]" size={28} /></td></tr>
            ) : quotations.length > 0 ? quotations.map(quotation => (
              <tr key={quotation._id} className="group">
                <td>
                  <input
                    type="checkbox"
                    checked={bulk.selected.has(quotation._id)}
                    onChange={() => bulk.toggle(quotation._id)}
                    aria-label={`Select ${quotation.quotationNumber}`}
                    className="w-4 h-4 accent-[#0176D3] cursor-pointer"
                  />
                </td>
                <td><span className="font-bold text-[#2E2E2E]">{quotation.quotationNumber}</span></td>
                <td>{quotation.accountId?.name || quotation.contactId?.name || '—'}</td>
                <td className="text-[#706E6B]">{quotation.product || '—'}</td>
                <td className="font-bold tabular-nums">{formatCurrency(quotation.totalAmount, quotation.currency)}</td>
                <td><span className={cn('sf-badge', STATUS_BADGE[quotation.status])}>{quotation.status === 'partially_invoiced' ? 'PART INVOICED' : kind === 'sales_order' && quotation.status === 'accepted' ? 'CONFIRMED' : quotation.status.toUpperCase()}</span></td>
                <td>{kind === 'sales_order'
                  ? <>{quotation.expectedDeliveryDate ? new Date(quotation.expectedDeliveryDate).toLocaleDateString() : '—'}{quotation.customerReference && <span className="block text-[11px] text-[#706E6B]">PO {quotation.customerReference}</span>}</>
                  : quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString() : '—'}</td>
                <td className="text-right">
                  <RowActionsMenu actions={[
                    { label: 'Mark Sent', icon: Send, onClick: () => handleMarkSent(quotation), hidden: quotation.status !== 'draft' },
                    {
                      label: sendingWhatsAppId === quotation._id ? 'Sending…' : 'Send via WhatsApp',
                      icon: MessageCircle, disabled: sendingWhatsAppId === quotation._id,
                      onClick: () => handleSendViaWhatsApp(quotation),
                      hidden: !(quotation.accountId?.phone || quotation.contactId?.phoneNumber) || quotation.status === 'converted',
                    },
                    {
                      label: sendingEmailId === quotation._id ? 'Sending…' : 'Send via Email',
                      icon: Mail, disabled: sendingEmailId === quotation._id,
                      onClick: () => handleSendViaEmail(quotation),
                      hidden: !(quotation.accountId?.email || quotation.contactId?.email) || quotation.status === 'converted',
                    },
                    { label: 'Mark Viewed', icon: MailOpen, onClick: () => handleMarkViewed(quotation), hidden: quotation.status !== 'sent' },
                    { label: 'Mark Accepted', icon: CheckCircle, onClick: () => handleMarkAccepted(quotation), hidden: kind === 'sales_order' || !['sent', 'viewed'].includes(quotation.status) },
                    { label: 'Confirm Order', icon: CheckCircle, onClick: () => handleMarkAccepted(quotation), hidden: kind !== 'sales_order' || !['draft', 'sent', 'viewed'].includes(quotation.status) },
                    { label: 'Convert to Sales Order', icon: ArrowRightCircle, onClick: () => convertTo(quotation, 'sales_order'), hidden: kind !== 'quotation' || ['rejected', 'expired', 'converted'].includes(quotation.status) },
                    { label: 'Convert to Proforma', icon: ArrowRightCircle, onClick: () => convertTo(quotation, 'proforma'), hidden: kind !== 'quotation' || ['rejected', 'expired', 'converted'].includes(quotation.status) },
                    { label: 'Create Delivery Challan', icon: ArrowRightCircle, onClick: () => navigate(`/dashboard/crm/delivery-challans?fromOrder=${quotation._id}`), hidden: kind !== 'sales_order' || quotation.status === 'rejected' },
                    { label: 'Mark Rejected', icon: XCircle, danger: true, onClick: () => handleMarkRejected(quotation), hidden: !['sent', 'viewed'].includes(quotation.status) },
                    {
                      label: convertingId === quotation._id ? 'Converting…' : 'Convert to Invoice',
                      icon: ArrowRightCircle, disabled: convertingId === quotation._id,
                      onClick: () => handleConvertToInvoice(quotation),
                      hidden: quotation.status === 'converted',
                    },
                    { label: previewingId === quotation._id ? 'Opening…' : 'Preview PDF', icon: Eye, disabled: previewingId === quotation._id, onClick: () => handlePreviewPdf(quotation) },
                    { label: downloadingId === quotation._id ? 'Downloading…' : 'Download PDF', icon: Download, disabled: downloadingId === quotation._id, onClick: () => handleDownloadPdf(quotation) },
                    { label: 'Delete', icon: Trash2, danger: true, onClick: () => handleDelete(quotation), hidden: quotation.status === 'converted' },
                  ]} />
                </td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="py-24 text-center">
                <FileSpreadsheet className="mx-auto mb-4 opacity-10" size={48} />
                <p className="text-sm font-bold text-[#706E6B] uppercase tracking-[0.2em]">No {doc.many.toLowerCase()} yet</p>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {billingQuote && (
        <BillingSliceModal
          title={`Invoice ${billingQuote.quotationNumber}`}
          total={Math.round(((billingQuote.subtotal || 0) - (billingQuote.discountAmount || 0)) * 100) / 100}
          billed={billingQuote.invoicedAmount || 0}
          currency={billingQuote.currency}
          submitting={convertingId === billingQuote._id}
          onClose={() => setBillingQuote(null)}
          onSubmit={submitConvert}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-2xl z-10 overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">New {doc.one}</h3>
              <button onClick={() => setShowModal(false)} className="text-[#706E6B] hover:text-[#2E2E2E]"><X size={18} /></button>
            </div>

            <form onSubmit={handleSave} className="p-4 sm:p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Account</label>
                  <SearchableSelect
                    value={form.accountId}
                    onChange={(accountId) => {
                      const account = accounts.find(a => a._id === accountId);
                      setForm(f => ({
                        ...f,
                        accountId,
                        placeOfSupply: account?.state || f.placeOfSupply,
                        customerGstin: account?.gstin || f.customerGstin,
                      }));
                    }}
                    options={accounts}
                    placeholder="Search accounts…"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Contact</label>
                  <SearchableSelect
                    value={form.contactId}
                    onChange={(contactId) => setForm(f => ({ ...f, contactId }))}
                    options={contacts}
                    placeholder="Search contacts…"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Product / Service</label>
                <input
                  value={form.product}
                  onChange={e => setForm(f => ({ ...f, product: e.target.value }))}
                  placeholder="e.g. Fintech Platform, ChatBot, CRM"
                  className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Place of Supply (State)</label>
                  <input
                    value={form.placeOfSupply}
                    onChange={e => setForm(f => ({ ...f, placeOfSupply: e.target.value }))}
                    placeholder="e.g. Karnataka"
                    className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Customer GSTIN</label>
                  <input
                    value={form.customerGstin}
                    onChange={e => setForm(f => ({ ...f, customerGstin: e.target.value.toUpperCase() }))}
                    placeholder="e.g. 29AAAAA0000A1Z5"
                    className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Line Items</label>
                  <button type="button" onClick={addLineItem} className="h-7 px-2.5 bg-[#EAF4FE] text-[#0176D3] rounded text-[11px] font-semibold hover:bg-[#D6EAFB] flex items-center gap-1"><Plus size={12} /> Add Item</button>
                </div>
                {form.lineItems.map((item, idx) => (
                  <div key={idx} className="p-2.5 bg-[#F9FAFB] border border-[#D8DDE6] rounded space-y-2">
                    <div className="flex items-center gap-2">
                      <ItemPicker value={item.itemId} onPick={(it) => setForm(f => applyItem(f, idx, it))} />
                      <input
                        value={item.description}
                        onChange={e => updateLineItem(idx, 'description', e.target.value)}
                        placeholder="Description"
                        className="flex-1 h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                      />
                      <input
                        type="number" min="0" step="0.01" value={item.quantity}
                        onChange={e => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-16 h-9 px-2 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                        title="Quantity"
                      />
                      <input
                        type="number" min="0" step="0.01" value={item.unitPrice}
                        onChange={e => updateLineItem(idx, 'unitPrice', parseFloat(e.target.value) || 0)}
                        className="w-24 h-9 px-2 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                        title="Unit Price"
                      />
                      {!form.taxProfileId && (
                        <input
                          type="number" min="0" max="100" step="0.01" value={item.taxRate ?? ''}
                          onChange={e => updateLineItem(idx, 'taxRate', e.target.value === '' ? '' : parseFloat(e.target.value))}
                          placeholder={`${Number(form.taxRate) || 0}%`}
                          className="w-16 h-9 px-2 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                          title="GST % for this line (empty = the rate below)"
                          aria-label="GST % for this line"
                        />
                      )}
                      <button type="button" onClick={() => removeLineItem(idx)} className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded shrink-0"><Trash2 size={14} /></button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        value={item.hsnCode || ''}
                        onChange={e => updateLineItem(idx, 'hsnCode', e.target.value)}
                        placeholder="HSN/SAC code"
                        className="w-32 h-8 px-2.5 bg-white border border-[#D8DDE6] rounded text-[12px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                      />
                      <input
                        value={item.remarks || ''}
                        onChange={e => updateLineItem(idx, 'remarks', e.target.value)}
                        placeholder="Remarks (optional)"
                        className="flex-1 h-8 px-2.5 bg-white border border-[#D8DDE6] rounded text-[12px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
                    {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <SalespersonSelect value={form.salespersonId} onChange={(salespersonId) => setForm(f => ({ ...f, salespersonId }))} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Tax Profile</label>
                  <select value={form.taxProfileId} onChange={e => setForm(f => ({ ...f, taxProfileId: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
                    <option value="">None (India GST)</option>
                    {taxProfiles.filter(p => p.isActive).map(p => <option key={p._id} value={p._id}>{p.name} ({p.rate}%)</option>)}
                  </select>
                </div>
              </div>

              {!form.taxProfileId && (
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Tax Rate (%) — India GST</label>
                  <input type="number" min="0" max="100" step="0.01" value={form.taxRate} onChange={e => setForm(f => ({ ...f, taxRate: parseFloat(e.target.value) || 0 }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Discount Type</label>
                  <select value={form.discountType} onChange={e => setForm(f => ({ ...f, discountType: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
                    <option value="flat">Flat Amount</option>
                    <option value="percentage">Percentage</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Discount {form.discountType === 'percentage' ? '(%)' : `(${form.currency})`}</label>
                  <input type="number" min="0" step="0.01" value={form.discountValue} onChange={e => setForm(f => ({ ...f, discountValue: parseFloat(e.target.value) || 0 }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Issue Date</label>
                  <input type="date" value={form.issueDate} onChange={e => setForm(f => ({ ...f, issueDate: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                </div>
                {kind === 'sales_order' ? (
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-semibold text-[#706E6B]">Deliver By</label>
                    <input type="date" value={form.expectedDeliveryDate || ''} onChange={e => setForm(f => ({ ...f, expectedDeliveryDate: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-semibold text-[#706E6B]">Valid Until</label>
                    <input type="date" value={form.validUntil} onChange={e => setForm(f => ({ ...f, validUntil: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                )}
              </div>
              {kind === 'sales_order' && (
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Customer's PO number</label>
                  <input value={form.customerReference || ''} onChange={e => setForm(f => ({ ...f, customerReference: e.target.value }))} className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Notes</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
              </div>

              <div className="p-4 bg-[#F4F6F9] rounded border border-[#D8DDE6] space-y-1 text-[13px]">
                <div className="flex justify-between"><span className="text-[#706E6B]">Subtotal</span><span className="font-bold">{formatCurrency(subtotal, form.currency)}</span></div>
                <div className="flex justify-between"><span className="text-[#706E6B]">Discount</span><span className="font-bold">-{formatCurrency(discountAmount, form.currency)}</span></div>
                <div className="flex justify-between"><span className="text-[#706E6B]">{selectedTaxProfile ? `${selectedTaxProfile.name} (${effectiveTaxRate}%)` : mixedRates ? 'GST (mixed rates)' : `GST (${lineRates[0] ?? effectiveTaxRate}%)`}</span><span className="font-bold">{formatCurrency(taxAmount, form.currency)}</span></div>
                <div className="flex justify-between text-[15px] pt-1 border-t border-[#D8DDE6]"><span className="font-bold">Total</span><span className="font-bold text-[#0176D3]">{formatCurrency(total, form.currency)}</span></div>
              </div>

              <div className="pt-2 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={saving} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
